import React, { useEffect, useState } from "react";
import { AlertCircle, CheckCircle, Loader2, Play, Eye, FileText, Download, Mail, Send, History, PlusCircle } from "lucide-react";
import GenericModal from "./GenericModal";
import useModalStore from "../../stores/modalStore";
import useUserStore from "../../stores/userStore";
import { 
    fetchPayPeriods, 
    previewPayroll, 
    runPayroll, 
    generatePayrollDocuments,
    getPayrollPdfDownloadUrl,
    emailPayrollPdf,
    emailAllPayrollPdfs,
    checkMissingEvents,
    suggestResolution,
    resolveMissingEvent
} from "../../services/payrollService";
import { fetchHistoricPayrolls } from "../../services/fetchHistoricPayrolls";
import getFirebaseAuthToken from "../../services/fireBaseAuthenticationService";
import { hasPermission } from "../../utils/permissions";

const parseNumericValue = (value) => {
    if (typeof value === "number") return Number.isFinite(value) ? value : 0;
    if (typeof value === "string") {
        const parsed = parseFloat(value.replace(/[$,]/g, ""));
        return Number.isFinite(parsed) ? parsed : 0;
    }
    return 0;
};

const formatCurrencyTotal = (value) =>
    `$${value.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const sumHistoryPayrollTotals = (employees) =>
    (employees || []).reduce(
        (acc, emp) => {
            acc.hours += parseNumericValue(emp.normal_hours);
            acc.overtimeHours += parseNumericValue(emp.overtime_hours);
            acc.services += parseNumericValue(emp.services_in_period);
            acc.tips += parseNumericValue(emp.tips ?? emp.income_in_period?.tips);
            acc.bonus += parseNumericValue(emp.bonus ?? emp.income_in_period?.bonus);
            acc.esstPay += parseNumericValue(emp.income_in_period?.esst_pay);
            acc.totalIncome += parseNumericValue(emp.income_in_period?.total_income);
            return acc;
        },
        { hours: 0, overtimeHours: 0, services: 0, tips: 0, bonus: 0, esstPay: 0, totalIncome: 0 }
    );

const RunPayrollModal = () => {
    const show = useModalStore(state => state.showRunPayrollModal);
    const closeModal = useModalStore(state => state.closeModal);
    const { currentUser, userIdToFirstName } = useUserStore();

    const [activeTab, setActiveTab] = useState("run"); // "run" or "history"
    const [payPeriods, setPayPeriods] = useState([]);
    const [selectedPeriod, setSelectedPeriod] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [isPreviewing, setIsPreviewing] = useState(false);
    const [isRunning, setIsRunning] = useState(false);
    const [isGeneratingDocs, setIsGeneratingDocs] = useState(false);
    const [isSendingEmails, setIsSendingEmails] = useState(false);
    const [isCheckingMissingEvents, setIsCheckingMissingEvents] = useState(false);
    const [missingEventsData, setMissingEventsData] = useState(null);
    const [resolvingIssue, setResolvingIssue] = useState(null); // the issue being resolved
    const [resolutionSuggestion, setResolutionSuggestion] = useState(null); // DaySmart suggestion
    const [resolutionTime, setResolutionTime] = useState(""); // user-entered or suggested time (HH:MM format)
    const [isLoadingSuggestion, setIsLoadingSuggestion] = useState(false);
    const [isSubmittingResolution, setIsSubmittingResolution] = useState(false);
    const [previewData, setPreviewData] = useState(null);
    const [runResult, setRunResult] = useState(null);
    const [documents, setDocuments] = useState(null);
    const [emailResults, setEmailResults] = useState(null);
    const [error, setError] = useState(null);
    const { employees } = useUserStore();
    
    // History tab state
    const [payrollHistory, setPayrollHistory] = useState([]);
    const [isLoadingHistory, setIsLoadingHistory] = useState(false);
    const [selectedHistoryRun, setSelectedHistoryRun] = useState(null);
    const backendUrl = process.env.REACT_APP_API_BASE_URL || "http://localhost:4000";

    // Fetch pay periods when modal opens
    useEffect(() => {
        if (show && payPeriods.length === 0) {
            loadPayPeriods();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [show]);

    // Reset state when modal closes
    useEffect(() => {
        if (!show) {
            setPreviewData(null);
            setRunResult(null);
            setDocuments(null);
            setEmailResults(null);
            setError(null);
            setSelectedPeriod(null);
            setActiveTab("run");
            setSelectedHistoryRun(null);
            setMissingEventsData(null);
            setResolvingIssue(null);
            setResolutionSuggestion(null);
            setResolutionTime("");
        }
    }, [show]);

    // Load history when switching to history tab
    const loadPayrollHistory = async () => {
        if (payrollHistory.length > 0) {
            // Already loaded - just select latest if nothing selected
            if (!selectedHistoryRun) {
                setSelectedHistoryRun(payrollHistory[0]);
            }
            return;
        }
        setIsLoadingHistory(true);
        setError(null);
        try {
            const history = await fetchHistoricPayrolls(getFirebaseAuthToken, backendUrl);
            // Sort by period_end descending (most recent first)
            history.sort((a, b) => new Date(b.period_end) - new Date(a.period_end));
            setPayrollHistory(history);
            // Auto-select the latest payroll
            if (history.length > 0) {
                setSelectedHistoryRun(history[0]);
            }
        } catch (err) {
            setError(`Failed to load payroll history: ${err.message}`);
        } finally {
            setIsLoadingHistory(false);
        }
    };

    const loadPayPeriods = async () => {
        setIsLoading(true);
        setError(null);
        try {
            const periods = await fetchPayPeriods();
            setPayPeriods(periods);
            // Default to last completed period
            const lastCompleted = periods.find(p => p.type === "last_completed");
            if (lastCompleted) {
                setSelectedPeriod(lastCompleted);
            }
        } catch (err) {
            setError(err.message);
        } finally {
            setIsLoading(false);
        }
    };

    const handlePreview = async () => {
        if (!selectedPeriod || !currentUser?.user_id) return;

        setIsPreviewing(true);
        setError(null);
        setPreviewData(null);
        setRunResult(null);

        try {
            const result = await previewPayroll(
                selectedPeriod.start_date,
                selectedPeriod.end_date,
                currentUser.user_id
            );
            setPreviewData(result);
        } catch (err) {
            setError(err.message);
        } finally {
            setIsPreviewing(false);
        }
    };

    const handleRunPayroll = async (skipMissingEventsCheck = false) => {
        if (!selectedPeriod || !currentUser?.user_id) return;

        setError(null);
        setMissingEventsData(null);

        // Check for missing timesheet events first (unless skipped)
        if (!skipMissingEventsCheck) {
            setIsCheckingMissingEvents(true);
            try {
                const missingResult = await checkMissingEvents(
                    selectedPeriod.start_date,
                    selectedPeriod.end_date,
                    currentUser.user_id
                );

                if (missingResult?.has_issues) {
                    setMissingEventsData(missingResult);
                    setIsCheckingMissingEvents(false);
                    return; // Stop here - user needs to review issues
                }
            } catch (err) {
                setError(`Failed to check for missing events: ${err.message}`);
                setIsCheckingMissingEvents(false);
                return;
            }
            setIsCheckingMissingEvents(false);
        }

        const confirmed = window.confirm(
            `Are you sure you want to run payroll for ${selectedPeriod.start_date} to ${selectedPeriod.end_date}?\n\nThis will save the payroll data to the database.`
        );
        if (!confirmed) return;

        setIsRunning(true);
        setRunResult(null);

        try {
            const result = await runPayroll(
                selectedPeriod.start_date,
                selectedPeriod.end_date,
                currentUser.user_id,
                true
            );
            setRunResult(result);
            setPreviewData(null);
            setMissingEventsData(null); // Clear missing events after successful run
        } catch (err) {
            setError(err.message);
        } finally {
            setIsRunning(false);
        }
    };

    const handleStartResolve = async (issue) => {
        setResolvingIssue(issue);
        setResolutionSuggestion(null);
        setResolutionTime("");
        setError(null);

        // Determine the location from the issue's events
        const locationId = issue.events?.[0]?.location_id || 1;

        // Fetch DaySmart suggestion
        setIsLoadingSuggestion(true);
        try {
            const suggestion = await suggestResolution(
                issue.user_id,
                issue.date,
                issue.likely_missing,
                locationId,
                currentUser.user_id
            );
            setResolutionSuggestion(suggestion);

            // Pre-fill the time input if we got a suggestion
            if (suggestion?.suggested_time) {
                const suggestedDate = new Date(suggestion.suggested_time);
                const hours = suggestedDate.getHours().toString().padStart(2, '0');
                const minutes = suggestedDate.getMinutes().toString().padStart(2, '0');
                setResolutionTime(`${hours}:${minutes}`);
            }
        } catch (err) {
            console.error("Failed to get suggestion:", err);
            // Not fatal - user can still enter time manually
        } finally {
            setIsLoadingSuggestion(false);
        }
    };

    const handleSubmitResolution = async () => {
        if (!resolvingIssue || !resolutionTime || !currentUser?.user_id) return;

        const eventType = resolvingIssue.likely_missing;
        if (!eventType) {
            setError("Cannot determine which event type is missing");
            return;
        }

        // Parse the time and combine with the issue date
        const [hours, minutes] = resolutionTime.split(':').map(Number);
        const issueDate = new Date(resolvingIssue.date + "T00:00:00");
        issueDate.setHours(hours, minutes, 0, 0);
        const eventTimestampMs = issueDate.getTime();

        const locationId = resolvingIssue.events?.[0]?.location_id || 1;

        setIsSubmittingResolution(true);
        setError(null);

        try {
            await resolveMissingEvent(
                resolvingIssue.user_id,
                eventType,
                eventTimestampMs,
                locationId,
                currentUser.user_id,
                `Resolved via payroll review UI`
            );

            // Clear resolution state
            setResolvingIssue(null);
            setResolutionSuggestion(null);
            setResolutionTime("");

            // Re-check for remaining issues
            setIsCheckingMissingEvents(true);
            const missingResult = await checkMissingEvents(
                selectedPeriod.start_date,
                selectedPeriod.end_date,
                currentUser.user_id
            );

            if (missingResult?.has_issues) {
                setMissingEventsData(missingResult);
            } else {
                setMissingEventsData(null);
            }
        } catch (err) {
            setError(`Failed to resolve issue: ${err.message}`);
        } finally {
            setIsSubmittingResolution(false);
            setIsCheckingMissingEvents(false);
        }
    };

    const handleCancelResolve = () => {
        setResolvingIssue(null);
        setResolutionSuggestion(null);
        setResolutionTime("");
    };

    const handleGenerateDocuments = async () => {
        if (!runResult?.run_id || !runResult?.employee_data) return;

        setIsGeneratingDocs(true);
        setError(null);

        try {
            const result = await generatePayrollDocuments(
                runResult.run_id,
                runResult.employee_data,
                selectedPeriod.start_date,
                selectedPeriod.end_date,
                currentUser.user_id,
                runResult.raw_timesheet_data
            );
            setDocuments(result);
        } catch (err) {
            setError(err.message);
        } finally {
            setIsGeneratingDocs(false);
        }
    };

    const handleDownloadPdf = async (gcsPath, employeeName) => {
        try {
            const downloadUrl = await getPayrollPdfDownloadUrl(gcsPath, currentUser.user_id);
            // Open in new tab to trigger download
            window.open(downloadUrl, '_blank');
        } catch (err) {
            setError(`Failed to download PDF for ${employeeName}: ${err.message}`);
        }
    };

    const handleEmailEmployee = async (doc) => {
        // Find employee email from employees list
        const employee = employees?.find(e => e.user_id === doc.user_id);
        const recipientEmail = employee?.email;

        if (!recipientEmail) {
            setError(`No email found for ${doc.employee}`);
            return;
        }

        const confirmed = window.confirm(
            `Send payroll PDF to ${doc.employee} at ${recipientEmail}?`
        );
        if (!confirmed) return;

        try {
            await emailPayrollPdf(
                doc.gcs_path,
                recipientEmail,
                doc.employee,
                selectedPeriod.start_date,
                selectedPeriod.end_date,
                currentUser.user_id
            );
            alert(`Email sent to ${recipientEmail}`);
        } catch (err) {
            setError(`Failed to email ${doc.employee}: ${err.message}`);
        }
    };

    const handleEmailAll = async () => {
        if (!documents?.pdfs) return;

        // Build list of documents with emails
        const docsWithEmails = documents.pdfs
            .filter(doc => !doc.error)
            .map(doc => {
                const employee = employees?.find(e => e.user_id === doc.user_id);
                return {
                    gcs_path: doc.gcs_path,
                    recipient_email: employee?.email,
                    employee_name: doc.employee
                };
            })
            .filter(doc => doc.recipient_email);

        if (docsWithEmails.length === 0) {
            setError("No employees with email addresses found");
            return;
        }

        const confirmed = window.confirm(
            `Send payroll PDFs to ${docsWithEmails.length} employees?\n\n` +
            docsWithEmails.map(d => `${d.employee_name}: ${d.recipient_email}`).join('\n')
        );
        if (!confirmed) return;

        setIsSendingEmails(true);
        setError(null);

        try {
            const result = await emailAllPayrollPdfs(
                docsWithEmails,
                selectedPeriod.start_date,
                selectedPeriod.end_date,
                currentUser.user_id
            );
            setEmailResults(result);
        } catch (err) {
            setError(err.message);
        } finally {
            setIsSendingEmails(false);
        }
    };

    if (!show) return null;

    // Access check
    if (!hasPermission('run_payroll')) {
        return (
            <GenericModal
                name="runPayrollModal"
                title="Payroll"
                hideButton={true}
                modalMaxWidth="max-w-md"
            >
                <div className="flex flex-col items-center justify-center p-8 text-center">
                    <AlertCircle className="w-16 h-16 text-red-500 mb-4" />
                    <h3 className="text-xl font-semibold text-gray-800 mb-2">Access Denied</h3>
                    <p className="text-gray-600">
                        Only managers and owners can access payroll features.
                    </p>
                    <button
                        onClick={() => closeModal("runPayrollModal")}
                        className="!mt-6 !px-4 !py-2 !text-sm !font-medium !text-gray-600 !bg-transparent !border !border-gray-200 !rounded-xl hover:!bg-gray-50 !transition-colors"
                    >
                        Close
                    </button>
                </div>
            </GenericModal>
        );
    }

    return (
        <GenericModal
            name="runPayrollModal"
            title="Payroll"
            hideButton={true}
            modalMaxWidth="max-w-4xl"
            stickyFooter={
                <div className="flex justify-between items-center">
                    <button
                        onClick={() => closeModal("runPayrollModal")}
                        className="!px-4 !py-2 !text-sm !font-medium !text-gray-600 !bg-transparent !border !border-gray-200 !rounded-xl hover:!bg-gray-50 !transition-colors"
                    >
                        Close
                    </button>
                    {activeTab === "run" && (
                        <div className="flex gap-2">
                            <button
                                onClick={handlePreview}
                                disabled={!selectedPeriod || isPreviewing || isRunning}
                                className="!flex !items-center !gap-2 !px-4 !py-2 !text-sm !font-semibold !text-white !bg-indigo-600 hover:!bg-indigo-700 !border-none !rounded-xl !shadow-sm hover:!shadow-md disabled:!opacity-40 disabled:!cursor-not-allowed !transition-all"
                            >
                                {isPreviewing ? (
                                    <Loader2 className="w-4 h-4 animate-spin" />
                                ) : (
                                    <Eye className="w-4 h-4" />
                                )}
                                Preview
                            </button>
                            <button
                                onClick={() => handleRunPayroll()}
                                disabled={!selectedPeriod || isPreviewing || isRunning || isCheckingMissingEvents}
                                className="!flex !items-center !gap-2 !px-4 !py-2 !text-sm !font-semibold !text-white !bg-emerald-600 hover:!bg-emerald-700 !border-none !rounded-xl !shadow-sm hover:!shadow-md disabled:!opacity-40 disabled:!cursor-not-allowed !transition-all"
                            >
                                {isRunning || isCheckingMissingEvents ? (
                                    <Loader2 className="w-4 h-4 animate-spin" />
                                ) : (
                                    <Play className="w-4 h-4" />
                                )}
                                {isCheckingMissingEvents ? "Checking..." : "Run & Save"}
                            </button>
                        </div>
                    )}
                </div>
            }
        >
            <div className="space-y-6">
                {/* Tab Switcher */}
                <div className="flex border-b border-gray-200">
                    <button
                        onClick={() => setActiveTab("run")}
                        className={`flex items-center gap-2 px-4 py-2 font-medium border-b-2 transition-colors ${
                            activeTab === "run"
                                ? "border-purple-600 text-purple-600"
                                : "border-transparent text-gray-500 hover:text-gray-700"
                        }`}
                    >
                        <PlusCircle className="w-4 h-4" />
                        Run Payroll
                    </button>
                    <button
                        onClick={() => {
                            setActiveTab("history");
                            loadPayrollHistory();
                        }}
                        className={`flex items-center gap-2 px-4 py-2 font-medium border-b-2 transition-colors ${
                            activeTab === "history"
                                ? "border-purple-600 text-purple-600"
                                : "border-transparent text-gray-500 hover:text-gray-700"
                        }`}
                    >
                        <History className="w-4 h-4" />
                        History
                    </button>
                </div>

                {/* History Tab Content */}
                {activeTab === "history" && (
                    <div className="space-y-4 min-h-[400px]">
                        {isLoadingHistory ? (
                            <div className="flex items-center justify-center p-8">
                                <Loader2 className="w-8 h-8 animate-spin text-purple-600" />
                            </div>
                        ) : payrollHistory.length === 0 ? (
                            <div className="text-center text-gray-500 py-8">
                                No payroll history found
                            </div>
                        ) : (
                            <>
                                <div className="grid gap-2 max-h-48 overflow-y-auto">
                                    {payrollHistory.map((run) => (
                                        <button
                                            key={run.run_id}
                                            onClick={() => setSelectedHistoryRun(run)}
                                            className={`p-3 text-left rounded border transition-colors ${
                                                selectedHistoryRun?.run_id === run.run_id
                                                    ? "border-purple-600 bg-purple-50"
                                                    : "border-gray-200 hover:border-gray-300"
                                            }`}
                                        >
                                            <div className="font-medium">
                                                {run.period_start} to {run.period_end}
                                            </div>
                                            <div className="text-sm text-gray-500">
                                                Run: {new Date(run.created_at).toLocaleString()}
                                            </div>
                                        </button>
                                    ))}
                                </div>

                                {selectedHistoryRun && (() => {
                                    const currentIndex = payrollHistory.findIndex(r => r.run_id === selectedHistoryRun.run_id);
                                    const historyTotals = sumHistoryPayrollTotals(selectedHistoryRun.employee_data);
                                    const goToHistoryPeriod = (direction) => {
                                        const newIndex = currentIndex + direction;
                                        if (newIndex >= 0 && newIndex < payrollHistory.length) {
                                            setSelectedHistoryRun(payrollHistory[newIndex]);
                                        }
                                    };
                                    return (
                                    <div className="border-t pt-4">
                                        <div className="flex justify-between items-center mb-3">
                                            <button
                                                onClick={() => goToHistoryPeriod(1)}
                                                disabled={currentIndex >= payrollHistory.length - 1}
                                                className="px-3 py-1 text-sm bg-gray-200 rounded hover:bg-gray-300 disabled:opacity-50 disabled:cursor-not-allowed"
                                            >
                                                ← Previous
                                            </button>
                                            <h3 className="font-semibold">
                                                Payroll: {selectedHistoryRun.period_start} to {selectedHistoryRun.period_end}
                                            </h3>
                                            <button
                                                onClick={() => goToHistoryPeriod(-1)}
                                                disabled={currentIndex <= 0}
                                                className="px-3 py-1 text-sm bg-gray-200 rounded hover:bg-gray-300 disabled:opacity-50 disabled:cursor-not-allowed"
                                            >
                                                Next →
                                            </button>
                                        </div>
                                        <div className="flex justify-end mb-2">
                                            <button
                                                onClick={() => handleDownloadPdf(
                                                    `payrolls/${selectedHistoryRun.period_start}_to_${selectedHistoryRun.period_end}/${selectedHistoryRun.run_id}/summary.html`,
                                                    "summary"
                                                )}
                                                className="flex items-center gap-1 px-3 py-1 bg-green-600 text-white text-sm rounded hover:bg-green-700"
                                                title="View Summary HTML"
                                            >
                                                <Eye className="w-4 h-4" />
                                                Summary
                                            </button>
                                        </div>
                                        <div className="overflow-x-auto">
                                            <table className="w-full border-collapse border border-gray-300 text-sm">
                                                <thead>
                                                    <tr className="bg-gray-100">
                                                        <th className="border border-gray-300 px-3 py-2 text-left">Employee</th>
                                                        <th className="border border-gray-300 px-3 py-2 text-right">Hours</th>
                                                        <th className="border border-gray-300 px-3 py-2 text-right">OT Hours</th>
                                                        <th className="border border-gray-300 px-3 py-2 text-right">Services</th>
                                                        <th className="border border-gray-300 px-3 py-2 text-right">Tips</th>
                                                        <th className="border border-gray-300 px-3 py-2 text-right">Bonus</th>
                                                        <th className="border border-gray-300 px-3 py-2 text-right">ESST Pay</th>
                                                        <th className="border border-gray-300 px-3 py-2 text-right">Total Income</th>
                                                        <th className="border border-gray-300 px-3 py-2 text-center">Documents</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {selectedHistoryRun.employee_data?.map((emp, idx) => {
                                                        // Handle both old format (income_in_period.* as "$X.XX") and new format (as number)
                                                        const tips = emp.tips ?? (typeof emp.income_in_period?.tips === 'string' 
                                                            ? parseFloat(emp.income_in_period.tips.replace(/[$,]/g, '')) 
                                                            : emp.income_in_period?.tips) ?? 0;
                                                        const bonus = emp.bonus ?? (typeof emp.income_in_period?.bonus === 'string'
                                                            ? parseFloat(emp.income_in_period.bonus.replace(/[$,]/g, ''))
                                                            : emp.income_in_period?.bonus) ?? 0;
                                                        const esstPay = typeof emp.income_in_period?.esst_pay === 'string'
                                                            ? parseFloat(emp.income_in_period.esst_pay.replace(/[$,]/g, ''))
                                                            : (emp.income_in_period?.esst_pay ?? 0);
                                                        const totalIncome = emp.income_in_period?.total_income ?? '$0.00';
                                                        const pdfPath = `payrolls/${selectedHistoryRun.period_start}_to_${selectedHistoryRun.period_end}/${selectedHistoryRun.run_id}/${emp.Employee?.replace(' ', '_')}.pdf`;
                                                        return (
                                                        <tr key={emp.user_id || idx} className={idx % 2 === 0 ? "bg-white" : "bg-gray-50"}>
                                                            <td className="border border-gray-300 px-3 py-2">{emp.Employee}</td>
                                                            <td className="border border-gray-300 px-3 py-2 text-right">{emp.normal_hours?.toFixed(2)}</td>
                                                            <td className="border border-gray-300 px-3 py-2 text-right">{emp.overtime_hours?.toFixed(2)}</td>
                                                            <td className="border border-gray-300 px-3 py-2 text-right">${emp.services_in_period?.toFixed(2)}</td>
                                                            <td className="border border-gray-300 px-3 py-2 text-right">${tips.toFixed(2)}</td>
                                                            <td className="border border-gray-300 px-3 py-2 text-right">${bonus.toFixed(2)}</td>
                                                            <td className="border border-gray-300 px-3 py-2 text-right">${esstPay.toFixed(2)}</td>
                                                            <td className="border border-gray-300 px-3 py-2 text-right font-semibold">{totalIncome}</td>
                                                            <td className="border border-gray-300 px-3 py-2 text-center">
                                                                <button
                                                                    onClick={() => handleDownloadPdf(pdfPath, emp.Employee)}
                                                                    className="px-2 py-1 bg-gray-100 text-gray-700 text-xs rounded hover:bg-gray-200"
                                                                    title="Download PDF"
                                                                >
                                                                    <Download className="w-3 h-3 inline" />
                                                                </button>
                                                            </td>
                                                        </tr>
                                                        );
                                                    })}
                                                </tbody>
                                                <tfoot>
                                                    <tr className="bg-gray-100 font-semibold">
                                                        <td className="border border-gray-300 px-3 py-2">Total</td>
                                                        <td className="border border-gray-300 px-3 py-2 text-right">{historyTotals.hours.toFixed(2)}</td>
                                                        <td className="border border-gray-300 px-3 py-2 text-right">{historyTotals.overtimeHours.toFixed(2)}</td>
                                                        <td className="border border-gray-300 px-3 py-2 text-right">{formatCurrencyTotal(historyTotals.services)}</td>
                                                        <td className="border border-gray-300 px-3 py-2 text-right">{formatCurrencyTotal(historyTotals.tips)}</td>
                                                        <td className="border border-gray-300 px-3 py-2 text-right">{formatCurrencyTotal(historyTotals.bonus)}</td>
                                                        <td className="border border-gray-300 px-3 py-2 text-right">{formatCurrencyTotal(historyTotals.esstPay)}</td>
                                                        <td className="border border-gray-300 px-3 py-2 text-right">{formatCurrencyTotal(historyTotals.totalIncome)}</td>
                                                        <td className="border border-gray-300 px-3 py-2"></td>
                                                    </tr>
                                                </tfoot>
                                            </table>
                                        </div>
                                    </div>
                                    );
                                })()}
                            </>
                        )}
                    </div>
                )}

                {/* Run Payroll Tab Content */}
                {activeTab === "run" && (
                    <>
                {/* Pay Period Selection */}
                <div>
                    <h3 className="text-lg font-semibold mb-3">Select Pay Period</h3>
                    {isLoading ? (
                        <div className="flex items-center gap-2 text-gray-600">
                            <Loader2 className="w-5 h-5 animate-spin" />
                            Loading pay periods...
                        </div>
                    ) : (
                        <div className="space-y-2">
                            {payPeriods.map((period) => (
                                <label
                                    key={period.type}
                                    className={`flex items-center p-3 border rounded cursor-pointer transition-colors ${
                                        selectedPeriod?.type === period.type
                                            ? "border-blue-500 bg-blue-50"
                                            : "border-gray-300 hover:bg-gray-50"
                                    }`}
                                >
                                    <input
                                        type="radio"
                                        name="payPeriod"
                                        checked={selectedPeriod?.type === period.type}
                                        onChange={() => {
                                            setSelectedPeriod(period);
                                            setPreviewData(null);
                                            setRunResult(null);
                                        }}
                                        className="mr-3"
                                    />
                                    <div>
                                        <div className="font-medium">{period.label}</div>
                                        <div className="text-sm text-gray-500">
                                            {period.start_date} to {period.end_date}
                                        </div>
                                    </div>
                                </label>
                            ))}
                        </div>
                    )}
                </div>

                {/* Error Display */}
                {error && (
                    <div className="p-4 bg-red-50 border border-red-200 rounded">
                        <div className="flex items-center gap-2 text-red-700">
                            <AlertCircle className="w-5 h-5" />
                            <span className="font-medium">Error</span>
                        </div>
                        <p className="mt-1 text-red-600">{error}</p>
                    </div>
                )}

                {/* Checking Missing Events Loading */}
                {isCheckingMissingEvents && (
                    <div className="p-4 bg-blue-50 border border-blue-200 rounded">
                        <div className="flex items-center gap-2 text-blue-700">
                            <Loader2 className="w-5 h-5 animate-spin" />
                            <span className="font-medium">Checking for missing timesheet events...</span>
                        </div>
                    </div>
                )}

                {/* Missing Events Warning */}
                {missingEventsData?.has_issues && (
                    <div className="p-4 bg-yellow-50 border border-yellow-200 rounded">
                        <div className="flex items-center gap-2 text-yellow-700 mb-3">
                            <AlertCircle className="w-5 h-5" />
                            <span className="font-medium">
                                Timesheet Issues Detected ({missingEventsData.issues.length} issue{missingEventsData.issues.length !== 1 ? 's' : ''})
                            </span>
                        </div>
                        <p className="text-yellow-700 text-sm mb-3">
                            The following employees have incomplete clock in/out records. You can fix each issue or proceed anyway.
                        </p>
                        <div className="max-h-80 overflow-y-auto mb-4">
                            <table className="w-full text-sm border-collapse">
                                <thead>
                                    <tr className="bg-yellow-100">
                                        <th className="border border-yellow-300 px-2 py-1 text-left">Employee</th>
                                        <th className="border border-yellow-300 px-2 py-1 text-left">Date</th>
                                        <th className="border border-yellow-300 px-2 py-1 text-center">Events</th>
                                        <th className="border border-yellow-300 px-2 py-1 text-left">Likely Missing</th>
                                        <th className="border border-yellow-300 px-2 py-1 text-left">Events on Day</th>
                                        <th className="border border-yellow-300 px-2 py-1 text-center">Action</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {missingEventsData.issues.map((issue, idx) => {
                                        const isResolving = resolvingIssue?.user_id === issue.user_id && resolvingIssue?.date === issue.date;
                                        return (
                                        <React.Fragment key={`${issue.user_id}-${issue.date}`}>
                                        <tr className={idx % 2 === 0 ? "bg-white" : "bg-yellow-50"}>
                                            <td className="border border-yellow-300 px-2 py-1">{issue.employee_name}</td>
                                            <td className="border border-yellow-300 px-2 py-1">{issue.date}</td>
                                            <td className="border border-yellow-300 px-2 py-1 text-center">{issue.event_count}</td>
                                            <td className="border border-yellow-300 px-2 py-1">
                                                {issue.likely_missing ? (
                                                    <span className="text-red-600 font-medium">{issue.likely_missing}</span>
                                                ) : (
                                                    <span className="text-gray-500">Unknown</span>
                                                )}
                                            </td>
                                            <td className="border border-yellow-300 px-2 py-1">
                                                <div className="text-xs space-y-1">
                                                    {issue.events?.map((evt, i) => (
                                                        <div key={evt.event_id || i} className="flex items-center gap-2 border-b border-yellow-200 pb-1 last:border-0 last:pb-0">
                                                            <span className={`font-medium ${evt.event_type === 'clock in' ? 'text-green-600' : 'text-red-600'}`}>
                                                                {evt.event_type === 'clock in' ? '→ IN' : '← OUT'}
                                                            </span>
                                                            <span className="text-gray-600">
                                                                {evt.event_time ? new Date(evt.event_time).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' }) : '-'}
                                                            </span>
                                                            <span className="text-gray-500">seq:{evt.sequence_number ?? '-'}</span>
                                                            <span className="text-gray-500">loc:{evt.location_id ?? '-'}</span>
                                                            <code className="bg-gray-100 px-1 rounded text-gray-700 select-all">
                                                                {evt.event_id ? evt.event_id.slice(0, 8) : 'N/A'}
                                                            </code>
                                                        </div>
                                                    ))}
                                                </div>
                                            </td>
                                            <td className="border border-yellow-300 px-2 py-1 text-center">
                                                {issue.likely_missing && (
                                                    <button
                                                        onClick={() => handleStartResolve(issue)}
                                                        disabled={isResolving || isSubmittingResolution}
                                                        className="px-2 py-1 bg-blue-600 text-white text-xs rounded hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed whitespace-nowrap"
                                                    >
                                                        {isResolving ? "Resolving..." : "Fix"}
                                                    </button>
                                                )}
                                            </td>
                                        </tr>
                                        {/* Inline Resolution Panel */}
                                        {isResolving && (
                                            <tr>
                                                <td colSpan={6} className="border border-blue-300 bg-blue-50 px-3 py-3">
                                                    <div className="space-y-3">
                                                        <div className="flex items-center gap-2 text-blue-700 font-medium text-sm">
                                                            <AlertCircle className="w-4 h-4" />
                                                            Insert missing {issue.likely_missing} for {issue.employee_name} on {issue.date}
                                                        </div>

                                                        {isLoadingSuggestion && (
                                                            <div className="flex items-center gap-2 text-blue-600 text-sm">
                                                                <Loader2 className="w-4 h-4 animate-spin" />
                                                                Checking DaySmart schedule...
                                                            </div>
                                                        )}

                                                        {resolutionSuggestion && !isLoadingSuggestion && (
                                                            <div className="text-sm">
                                                                {resolutionSuggestion.suggested_time ? (
                                                                    <div className="p-2 bg-green-50 border border-green-200 rounded">
                                                                        <span className="text-green-700 font-medium">
                                                                            DaySmart suggestion: {new Date(resolutionSuggestion.suggested_time).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}
                                                                        </span>
                                                                        <span className="text-green-600 text-xs ml-2">
                                                                            ({resolutionSuggestion.suggestion_source === 'daysmart_last_appointment_end'
                                                                                ? 'last appointment end time'
                                                                                : resolutionSuggestion.suggestion_source === 'daysmart_first_appointment_start'
                                                                                    ? 'first appointment start time'
                                                                                    : resolutionSuggestion.suggestion_source})
                                                                        </span>
                                                                    </div>
                                                                ) : (
                                                                    <div className="p-2 bg-gray-50 border border-gray-200 rounded text-gray-600">
                                                                        No DaySmart schedule found for this date. Enter a time manually.
                                                                    </div>
                                                                )}

                                                                {resolutionSuggestion.appointments?.length > 0 && (
                                                                    <details className="mt-2">
                                                                        <summary className="text-xs text-blue-600 cursor-pointer hover:underline">
                                                                            View {resolutionSuggestion.appointments.length} appointment(s)
                                                                        </summary>
                                                                        <div className="mt-1 space-y-1 pl-2 border-l-2 border-blue-200">
                                                                            {resolutionSuggestion.appointments.map((appt, i) => (
                                                                                <div key={i} className="text-xs text-gray-600">
                                                                                    {appt.start_time && new Date(appt.start_time).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}
                                                                                    {' - '}
                                                                                    {appt.end_time && new Date(appt.end_time).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}
                                                                                    {appt.client_name && ` • ${appt.client_name}`}
                                                                                    {appt.service_name && ` • ${appt.service_name}`}
                                                                                </div>
                                                                            ))}
                                                                        </div>
                                                                    </details>
                                                                )}
                                                            </div>
                                                        )}

                                                        <div className="flex items-center gap-3">
                                                            <label className="text-sm text-gray-700 font-medium whitespace-nowrap">
                                                                {issue.likely_missing === 'clock in' ? 'Clock In' : 'Clock Out'} Time:
                                                            </label>
                                                            <input
                                                                type="time"
                                                                value={resolutionTime}
                                                                onChange={(e) => setResolutionTime(e.target.value)}
                                                                className="px-2 py-1 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-blue-400 focus:outline-none"
                                                            />
                                                            <button
                                                                onClick={handleSubmitResolution}
                                                                disabled={!resolutionTime || isSubmittingResolution}
                                                                className="flex items-center gap-1 px-3 py-1 bg-green-600 text-white text-sm rounded hover:bg-green-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
                                                            >
                                                                {isSubmittingResolution ? (
                                                                    <Loader2 className="w-3 h-3 animate-spin" />
                                                                ) : (
                                                                    <CheckCircle className="w-3 h-3" />
                                                                )}
                                                                {isSubmittingResolution ? "Inserting..." : "Insert Event"}
                                                            </button>
                                                            <button
                                                                onClick={handleCancelResolve}
                                                                disabled={isSubmittingResolution}
                                                                className="px-3 py-1 bg-gray-300 text-gray-700 text-sm rounded hover:bg-gray-400 disabled:opacity-50"
                                                            >
                                                                Cancel
                                                            </button>
                                                        </div>
                                                    </div>
                                                </td>
                                            </tr>
                                        )}
                                        </React.Fragment>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                        <div className="flex justify-between items-center">
                            <p className="text-yellow-700 text-sm">
                                Fix issues above, or proceed anyway.
                            </p>
                            <div className="flex gap-2">
                                <button
                                    onClick={() => { setMissingEventsData(null); handleCancelResolve(); }}
                                    className="px-3 py-1 bg-gray-300 text-gray-700 text-sm rounded hover:bg-gray-400"
                                >
                                    Dismiss
                                </button>
                                <button
                                    onClick={() => handleRunPayroll(true)}
                                    disabled={isSubmittingResolution}
                                    className="px-3 py-1 bg-yellow-600 text-white text-sm rounded hover:bg-yellow-700 disabled:bg-gray-400"
                                >
                                    Proceed Anyway
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                {/* Run Result */}
                {runResult && (
                    <div className={`p-4 border rounded ${runResult.has_errors ? 'bg-red-50 border-red-200' : 'bg-green-50 border-green-200'}`}>
                        <div className="flex items-center justify-between mb-2">
                            <div className={`flex items-center gap-2 ${runResult.has_errors ? 'text-red-700' : 'text-green-700'}`}>
                                {runResult.has_errors ? (
                                    <AlertCircle className="w-5 h-5" />
                                ) : (
                                    <CheckCircle className="w-5 h-5" />
                                )}
                                <span className="font-medium">
                                    {runResult.has_errors ? 'Payroll Failed - Not Saved' : 'Payroll Saved Successfully'}
                                </span>
                            </div>
                            {!runResult.has_errors && !documents && (
                                <button
                                    onClick={handleGenerateDocuments}
                                    disabled={isGeneratingDocs}
                                    className="flex items-center gap-2 px-3 py-1 bg-purple-600 text-white text-sm rounded hover:bg-purple-700 disabled:bg-gray-400"
                                >
                                    {isGeneratingDocs ? (
                                        <Loader2 className="w-4 h-4 animate-spin" />
                                    ) : (
                                        <FileText className="w-4 h-4" />
                                    )}
                                    Generate PDFs
                                </button>
                            )}
                        </div>
                        {runResult.error && (
                            <p className="text-red-600 text-sm font-medium mb-1">
                                {runResult.error}
                            </p>
                        )}
                        {runResult.run_id && (
                            <p className="text-green-600 text-sm">
                                Run ID: {runResult.run_id}
                            </p>
                        )}
                        <p className={`text-sm ${runResult.has_errors ? 'text-red-600' : 'text-green-600'}`}>
                            {runResult.employee_count} employees processed
                        </p>
                        {runResult.daysmart_status && (
                            <p className={`text-sm mt-1 ${
                                runResult.daysmart_status.services?.startsWith('error') || 
                                runResult.daysmart_status.tips?.startsWith('error') 
                                    ? 'text-red-600' : 'text-green-600'
                            }`}>
                                DaySmart: Services {runResult.daysmart_status.services}, Tips {runResult.daysmart_status.tips}
                            </p>
                        )}
                    </div>
                )}

                {/* Generated Documents */}
                {documents && (
                    <div className="p-4 bg-purple-50 border border-purple-200 rounded">
                        <div className="flex items-center justify-between mb-3">
                            <div className="flex items-center gap-2 text-purple-700">
                                <FileText className="w-5 h-5" />
                                <span className="font-medium">PDFs Generated ({documents.pdfs?.length || 0})</span>
                            </div>
                            <div className="flex gap-2">
                                {documents.summary_html && (
                                    <button
                                        onClick={() => handleDownloadPdf(documents.summary_html, "summary")}
                                        className="flex items-center gap-1 px-3 py-1 bg-green-600 text-white text-sm rounded hover:bg-green-700"
                                        title="View Summary HTML"
                                    >
                                        <Eye className="w-4 h-4" />
                                        Summary
                                    </button>
                                )}
                                {documents.table_insert_js && (
                                    <button
                                        onClick={() => handleDownloadPdf(documents.table_insert_js, "table_insert")}
                                        className="flex items-center gap-1 px-3 py-1 bg-orange-600 text-white text-sm rounded hover:bg-orange-700"
                                        title="Download isolved JS"
                                    >
                                        <Download className="w-4 h-4" />
                                        isolved JS
                                    </button>
                                )}
                                <button
                                    onClick={handleEmailAll}
                                    disabled={isSendingEmails}
                                    className="flex items-center gap-2 px-3 py-1 bg-blue-600 text-white text-sm rounded hover:bg-blue-700 disabled:bg-gray-400"
                                >
                                    {isSendingEmails ? (
                                        <Loader2 className="w-4 h-4 animate-spin" />
                                    ) : (
                                        <Send className="w-4 h-4" />
                                    )}
                                    Email All
                                </button>
                            </div>
                        </div>
                        <div className="space-y-2 max-h-60 overflow-y-auto">
                            {documents.pdfs?.map((doc, idx) => (
                                <div key={idx} className="flex items-center justify-between p-2 bg-white rounded border">
                                    <span className="text-sm font-medium">{doc.employee}</span>
                                    {doc.error ? (
                                        <span className="text-red-500 text-sm">{doc.error}</span>
                                    ) : (
                                        <div className="flex gap-2">
                                            <button
                                                onClick={() => handleDownloadPdf(doc.gcs_path, doc.employee)}
                                                className="flex items-center gap-1 px-2 py-1 bg-gray-100 text-gray-700 text-xs rounded hover:bg-gray-200"
                                                title="Download PDF"
                                            >
                                                <Download className="w-3 h-3" />
                                                Download
                                            </button>
                                            <button
                                                onClick={() => handleEmailEmployee(doc)}
                                                className="flex items-center gap-1 px-2 py-1 bg-blue-100 text-blue-700 text-xs rounded hover:bg-blue-200"
                                                title="Email to employee"
                                            >
                                                <Mail className="w-3 h-3" />
                                                Email
                                            </button>
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {/* Email Results */}
                {emailResults && (
                    <div className="p-4 bg-blue-50 border border-blue-200 rounded">
                        <div className="flex items-center gap-2 text-blue-700 mb-2">
                            <Mail className="w-5 h-5" />
                            <span className="font-medium">Email Results</span>
                        </div>
                        <p className="text-blue-600 text-sm">
                            Sent: {emailResults.successful} / {emailResults.total}
                        </p>
                        {emailResults.failed > 0 && (
                            <p className="text-red-600 text-sm">
                                Failed: {emailResults.failed}
                            </p>
                        )}
                        <div className="mt-2 space-y-1 max-h-40 overflow-y-auto">
                            {emailResults.results?.map((r, idx) => (
                                <div key={idx} className="text-xs flex items-center gap-2">
                                    {r.success ? (
                                        <CheckCircle className="w-3 h-3 text-green-500" />
                                    ) : (
                                        <AlertCircle className="w-3 h-3 text-red-500" />
                                    )}
                                    <span>{r.employee}: {r.success ? r.recipient : r.error}</span>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {/* Preview Data */}
                {previewData && (
                    <div>
                        <h3 className="text-lg font-semibold mb-3 flex items-center gap-2">
                            <Eye className="w-5 h-5" />
                            Preview Results ({previewData.employee_count} employees)
                        </h3>
                        <div className="overflow-x-auto">
                            <table className="w-full border-collapse border border-gray-300 text-sm">
                                <thead>
                                    <tr className="bg-gray-100">
                                        <th className="border border-gray-300 px-3 py-2 text-left">Employee</th>
                                        <th className="border border-gray-300 px-3 py-2 text-right">Hours</th>
                                        <th className="border border-gray-300 px-3 py-2 text-right">OT Hours</th>
                                        <th className="border border-gray-300 px-3 py-2 text-right">Services</th>
                                        <th className="border border-gray-300 px-3 py-2 text-right">Service Rate</th>
                                        <th className="border border-gray-300 px-3 py-2 text-right">Tips</th>
                                        <th className="border border-gray-300 px-3 py-2 text-right">Bonus</th>
                                        <th className="border border-gray-300 px-3 py-2 text-right">Total</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {previewData.employee_data?.map((emp, idx) => (
                                        <tr key={emp.user_id || idx} className={idx % 2 === 0 ? "bg-white" : "bg-gray-50"}>
                                            <td className="border border-gray-300 px-3 py-2">
                                                {emp.Employee || userIdToFirstName[emp.user_id] || `ID: ${emp.user_id}`}
                                            </td>
                                            <td className="border border-gray-300 px-3 py-2 text-right">
                                                {emp.normal_hours?.toFixed(2) || "0.00"}
                                            </td>
                                            <td className="border border-gray-300 px-3 py-2 text-right">
                                                {emp.overtime_hours?.toFixed(2) || "0.00"}
                                            </td>
                                            <td className="border border-gray-300 px-3 py-2 text-right">
                                                ${emp.services_in_period?.toFixed(2) || "0.00"}
                                            </td>
                                            <td className="border border-gray-300 px-3 py-2 text-right">
                                                ${emp.service_rate?.toFixed(2) || "0.00"}/hr
                                            </td>
                                            <td className="border border-gray-300 px-3 py-2 text-right">
                                                ${emp.tips?.toFixed(2) || "0.00"}
                                            </td>
                                            <td className="border border-gray-300 px-3 py-2 text-right">
                                                ${emp.bonus?.toFixed(2) || "0.00"}
                                            </td>
                                            <td className="border border-gray-300 px-3 py-2 text-right font-medium">
                                                {emp.income_in_period?.total_income || "$0.00"}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                        <p className="mt-3 text-sm text-gray-500">
                            This is a preview. Click "Run & Save" to save this payroll to the database.
                        </p>
                    </div>
                )}

                {/* Instructions */}
                {!previewData && !runResult && !error && (
                    <div className="p-4 bg-blue-50 border border-blue-200 rounded">
                        <h4 className="font-medium text-blue-800 mb-2">How to Run Payroll</h4>
                        <ol className="list-decimal list-inside text-blue-700 space-y-1">
                            <li>Select a pay period above</li>
                            <li>Click "Preview" to see calculated payroll data</li>
                            <li>Review the results for accuracy</li>
                            <li>Click "Run & Save" to save the payroll to the database</li>
                            <li>Click "Generate PDFs" to create employee pay summaries</li>
                            <li>Download PDFs or email them to employees</li>
                        </ol>
                        <p className="mt-3 text-sm text-blue-600">
                            Payroll includes timesheet hours, DaySmart services/tips, ESST calculations, 
                            and bonus calculations based on service rate.
                        </p>
                    </div>
                )}
                </>
                )}
            </div>
        </GenericModal>
    );
};

export default RunPayrollModal;
